import { useId } from "react";
import { styled } from "@pigment-css/react";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";
import { ToastCapsule, ToastMessage } from "./ToastCapsule";

const Root = styled(ToastCapsule)(({ theme }) => ({
  gap: theme.fieldSalesTwo.spacing[12],
}));

const UndoButton = styled("button")(({ theme }) => ({
  border: "none",
  background: "transparent",
  padding: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  color: theme.fieldSalesTwo.colors.accent.action,
  cursor: "pointer",
  whiteSpace: "nowrap",
  WebkitTapHighlightColor: "transparent",
  ...pressInteraction(theme),
}));

interface UndoToastProps {
  message: string;
  undoLabel: string;
  onUndo: () => void;
}

/**
 * Action-bearing confirmation with an undo affordance. It has no auto-dismiss: whoever opened it
 * owns a grace timer, and the banner has to stay up for as long as that timer can still be undone.
 *
 * The button takes its name from [message] as well as [undoLabel], because banners for two
 * different actions can be up together and every one of them labels its button "undo".
 */
export function UndoToast({ message, undoLabel, onUndo }: UndoToastProps) {
  const messageId = useId();
  const buttonId = useId();

  return (
    <Root role="status" aria-live="polite">
      <ToastMessage id={messageId}>{message}</ToastMessage>
      <UndoButton type="button" id={buttonId} aria-labelledby={`${messageId} ${buttonId}`} onClick={onUndo}>
        {undoLabel}
      </UndoButton>
    </Root>
  );
}
